import { requestPasswordReset } from "@vetty/api-sdk";
import { setFieldError, setFieldValue, setLoading } from "@/stores";
import { axiosInstance, validateEmail } from "@/helpers";
import { useForm } from "@/hooks";
import axios from "axios";

/**
 * useForgotPasswordForm Custom Hook
 *
 * Anti-enumeration: HTTP 200 with resetEmailSent is always treated as success
 * (whether or not the account exists). Network / 5xx / 429 show a generic error.
 */
export const useForgotPasswordForm = () => {
  const { values, loading } = useForm();

  const validateEmailField = () => {
    const email = String(values.email ?? "");
    const emailError = !validateEmail(email);

    return {
      emailError,
      isValid: !emailError,
    };
  };

  const handleSuccess = () => {
    setFieldValue("status", true);
  };

  const handleError = (message = "Something went wrong!") => {
    setFieldValue("status", false);
    setFieldError("email", message);
  };

  const submitForgotPassword = async () => {
    setLoading(true);

    const validation = validateEmailField();

    if (!validation.isValid) {
      setFieldError("email", validation.emailError ? "Invalid e-mail" : "");
      setLoading(false);
      return;
    }

    try {
      const email = String(values.email ?? "");
      const { data } = await requestPasswordReset(axiosInstance, { email });

      if (data?.success) {
        handleSuccess();
      } else {
        handleError(data?.message || "Something went wrong!");
      }
    } catch (error) {
      console.error("Password reset error:", error);
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 429) {
          handleError("Too many requests. Please try again later.");
        } else if (!error.response) {
          handleError("Network error. Please try again.");
        } else if (status && status >= 500) {
          handleError("Something went wrong. Please try again later.");
        } else {
          handleError(
            (error.response.data as { message?: string })?.message ||
              "Something went wrong!",
          );
        }
      } else {
        handleError();
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    submitForgotPassword,
    loading,
    values,
  };
};
